import React from "react";
import type { Person } from "../rounds/PersonPicker";
import { CLOSE_REASONS } from "../rounds/LoomSheet";
import { fmtDay, partName, rupees } from "../rates/rateModel";
import type { SheetLine } from "../sheet/sheetModel";
import { useKT } from "../i18n/kt";
import { dayMonth, DailyRow, needsPart, needsPeople, rowStatus } from "./dailyModel";

/** The helper list's "no helper needed" choice (an empty value means the weaver works alone). */
const NOT_NEEDED = "__NOT_NEEDED__";

const reasonLabel = (k?: string) =>
  CLOSE_REASONS.find((r) => r.key === k)?.label || "Closed";

type Calc = { status?: string; total?: string | number | null };

/** Options for a person select: the roster, plus today's person if the roster on this device lacks them. */
const withCurrent = (
  roster: Person[],
  current?: { id: string; code: string; name?: string } | null,
): Person[] =>
  current && current.id && !roster.some((p) => p.id === current.id)
    ? [
        { id: current.id, code: current.code, name: current.name || "" },
        ...roster,
      ]
    : roster;

/**
 * The office view: the old DAILY WORK sheet as a table. Type inches down the column (Enter moves
 * down, Tab moves across), change weaver / helper / part for today in place, Ctrl+S saves all.
 */
const DailyTable = ({
  rows,
  lines,
  roster,
  types,
  previews,
  date,
  onPatch,
  onType,
  onPaste,
  onEnter,
  registerInput,
  onOpen,
  onReopen,
  onStart,
  confirmed,
  onConfirm,
}: {
  /** Looms whose people were confirmed or changed on this screen. */
  confirmed: Set<string>;
  onConfirm: (loomId: string) => void;
  rows: DailyRow[];
  lines: Map<string, SheetLine>;
  roster: Person[];
  types: any[];
  previews: Map<string, Calc>;
  date: string;
  onPatch: (loomId: string, patch: Partial<SheetLine>) => void;
  onType: (loomId: string, inches: string) => void;
  onPaste: (loomId: string, text: string) => boolean;
  onEnter: (loomId: string) => void;
  registerInput: (loomId: string, el: HTMLInputElement | null) => void;
  onOpen: (loomId: string) => void;
  onReopen: (loomId: string) => void;
  onStart: (loomId: string) => void;
}) => {
  const { t, lang } = useKT();
  const typeName = (id?: string) => partName(types.find((x) => x.id === id), lang, t);
  const who = (p?: { code: string; name?: string } | null) =>
    p ? `${p.code} ${p.name || ""}`.trim() : "";

  return (
    <div className="kd-table-wrap">
      <table className="kd-table">
        <thead>
          <tr>
            <th scope="col">{t("Loom")}</th>
            <th scope="col">{t("Saree")}</th>
            <th scope="col">{t("Design")}</th>
            <th scope="col">{t("Weaver")}</th>
            <th scope="col">{t("Helper")}</th>
            <th scope="col">{t("Part")}</th>
            <th scope="col" className="is-num">
              {t("Inches")}
            </th>
            <th scope="col" className="is-num">
              ₹
            </th>
            <th scope="col">{t("Status")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const line = lines.get(r.loomId);
            const loomCell = (
              <th scope="row">
                <button
                  type="button"
                  className="kd-cell-link"
                  onClick={() => onOpen(r.loomId)}
                  title={t("Open loom")}
                >
                  {r.loomCode}
                </button>
              </th>
            );
            if (r.kind !== "RUN" || !line) {
              return (
                <tr key={r.loomId} className={`is-${r.kind}`}>
                  {loomCell}
                  <td className="kd-dim">—</td>
                  <td className="kd-dim">—</td>
                  <td colSpan={4} className="kd-dim">
                    <span className="kd-cell-text">
                      {r.kind === "CLOSED"
                        ? `${t("Closed")} · ${t(reasonLabel(r.reason))} · ${r.closedSince && r.closedSince < date ? t("since {{day}}", { day: fmtDay(r.closedSince) }) : t("today")}`
                        : r.moveStart
                          ? `${t("Saree {{n}} is on it from {{day}}", { n: r.moveStart.sareeNumber, day: fmtDay(r.moveStart.currentStart) })}${
                              r.moveStart.canMove ? "" : ` · ${t("Ask the office to move saree {{n}}'s start", { n: r.moveStart.sareeNumber })}`
                            }`
                          : t("No saree yet")}
                    </span>
                  </td>
                  <td />
                  <td>
                    {r.kind === "CLOSED" ? (
                      <button
                        type="button"
                        className="kd-btn kd-btn--line kd-btn--sm"
                        onClick={() => onReopen(r.loomId)}
                      >
                        {t("Reopen")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="kd-btn kd-btn--sm"
                        onClick={() => onStart(r.loomId)}
                      >
                        {r.moveStart ? (r.moveStart.canMove ? t("Move start") : t("Open loom")) : t("Start saree")}
                      </button>
                    )}
                  </td>
                </tr>
              );
            }
            const locked = line.status === "SAVED" || line.status === "SAVING";
            const preview = previews.get(r.loomId);
            const unconfirmed = needsPeople(r, line, confirmed);
            const status = rowStatus(r, line, preview, !unconfirmed);
            const weaver =
              roster.find((p) => p.id === line.weaverId) || r.weaver;
            const helper = line.helperId
              ? roster.find((p) => p.id === line.helperId) || r.helper
              : null;
            const amount =
              line.status === "SAVED"
                ? r.unrated
                  ? "—"
                  : rupees(r.savedAmount, 0)
                : Number(line.inches) > 0
                  ? preview?.status === "OK" && preview.total != null
                    ? rupees(preview.total, 0)
                    : preview
                      ? "—"
                      : "…"
                  : "";
            return (
              <tr
                key={r.loomId}
                className={`is-RUN${locked ? " is-saved" : ""}${line.status === "ERROR" ? " is-err" : ""}${unconfirmed ? " is-unconfirmed" : ""}${unconfirmed && r.helperChanged ? " is-helper-changed" : ""}`}
              >
                {loomCell}
                <td className="kw-code">
                  {r.sareeNumber}
                  {r.startMove && !locked ? (
                    <small className="kd-move kd-cell-note">
                      {t("starts {{from}} → {{to}}", { from: dayMonth(r.startMove.currentStart, lang), to: dayMonth(date, lang) })}
                      {r.startMove.needsApproval ? ` · ${t("goes to Approvals")}` : ""}
                    </small>
                  ) : null}
                </td>
                <td>{r.designNumber}</td>
                <td>
                  {locked ? (
                    <span className="kd-cell-text">{who(weaver)}</span>
                  ) : (
                    <select
                      className="kd-cell-select"
                      value={line.weaverId}
                      onChange={(e) =>
                        onPatch(r.loomId, { weaverId: e.target.value })
                      }
                      aria-label={`${t("Weaver")} ${t("Loom")} ${r.loomCode}`}
                    >
                      <option value="">{t("Choose the weaver")}</option>
                      {withCurrent(roster, r.weaver).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code} {p.name}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>
                  {locked ? (
                    <span className="kd-cell-text">
                      {helper ? who(helper) : line.helperMode === "NOT_NEEDED" ? t("Not needed") : t("Alone")}
                    </span>
                  ) : (
                    <select
                      className="kd-cell-select"
                      value={line.helperId || (line.helperMode === "NOT_NEEDED" ? NOT_NEEDED : "")}
                      onChange={(e) =>
                        onPatch(
                          r.loomId,
                          e.target.value === NOT_NEEDED
                            ? { helperId: "", helperMode: "NOT_NEEDED" }
                            : e.target.value
                              ? { helperId: e.target.value }
                              : { helperId: "", helperMode: "ALONE" },
                        )
                      }
                      aria-label={`${t("Helper")} ${t("Loom")} ${r.loomCode}`}
                    >
                      <option value="">{t("Alone")}</option>
                      <option value={NOT_NEEDED}>{t("Not needed")}</option>
                      {withCurrent(roster, r.helper).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code} {p.name}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>
                  {locked ? (
                    <span className="kd-cell-text">
                      {typeName(line.workTypeId)}
                    </span>
                  ) : (
                    <select
                      className={`kd-cell-select${needsPart(line) ? " is-needed" : ""}`}
                      value={line.workTypeId}
                      onChange={(e) =>
                        onPatch(r.loomId, { workTypeId: e.target.value })
                      }
                      aria-label={`${t("Part")} ${t("Loom")} ${r.loomCode}`}
                    >
                      {/* A back-filled saree with no known starting part: picked by hand, never guessed. */}
                      {!line.workTypeId ? <option value="">{t("Choose the part")}</option> : null}
                      {types.map((w) => (
                        <option key={w.id} value={w.id}>
                          {partName(w, lang, t)}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="is-num">
                  {locked ? (
                    <span className="kd-inch is-saved">{line.inches}</span>
                  ) : (
                    <input
                      ref={(el) => registerInput(r.loomId, el)}
                      className={`kd-inch${line.status === "ERROR" ? " is-err" : ""}`}
                      inputMode="decimal"
                      value={line.inches}
                      aria-label={`${t("Inches")} ${t("Loom")} ${r.loomCode}`}
                      onChange={(e) =>
                        onType(r.loomId, e.target.value.replace(/[^\d.]/g, ""))
                      }
                      onPaste={(e) => {
                        if (onPaste(r.loomId, e.clipboardData.getData("text")))
                          e.preventDefault();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          onEnter(r.loomId);
                        }
                      }}
                    />
                  )}
                </td>
                <td className="is-num">{amount}</td>
                <td>
                  {status ? (
                    <span
                      className={`kd-chip is-${status.tone}`}
                      title={line.message || undefined}
                    >
                      {t(status.label, status.n ? { n: status.n } : undefined)}
                    </span>
                  ) : null}
                  {unconfirmed ? (
                    <button
                      type="button"
                      className="kd-btn kd-btn--line kd-btn--sm kd-same"
                      title={r.helperChanged ? t("The helper changed this week. Check before saving.") : undefined}
                      onClick={() => onConfirm(r.loomId)}
                    >
                      {t("Same as yesterday ✓")}
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DailyTable;
